'use strict';

import { orderBy, find, intersection } from 'lodash';
import { DateTime } from 'luxon';

import { loadWidget } from '../utils/load-widget';
import { getCookie } from '../../utils/cookie-handler';
import Calus from 'calus';
import { getEvents } from '../../utils/api';
import { isFavorite, getUserFavorites } from '../../utils/favorites';

const now = DateTime.now().startOf('day');
const todayString = now.toFormat('yyyy-MM-dd');

const specialOffersText = 'Special Offers';

let rawEvents = [];

loadWidget('calendar-calus', Calus);

loadWidget('performance-calendar', {
    data: function () {
        return {
            ViewModel: {
                Loading: true,
                Performances: [],
                FilteredEvents: [],
                Groups: [],
                FavoritesView: false,
                EligibleForCYO: false,
            },
        };
    },
    mounted: function() {
        getEvents().then(events => {
            events = events.filter(event => {
                return DateTime.fromISO(event.perf_date).plus({ hours: 2, minutes: 30 }) > DateTime.now();
            });
            const query = this.getURLQuery();

            if ('View' in query === false || query.View !== 'Favorites') {
                return events;
            }

            this.ViewModel.FavoritesView = true;

            const promises = events.map(item => {
                return isFavorite(item.perf_no).then(status => {
                    return status ? item : null;
                });
            });

            return Promise.all(promises).then(events => {
                return events.filter(event => {
                    return event !== null;
                });
            });
        }).then(events => {
            events = events.filter(event => {
                return DateTime.fromISO(event.perf_date) > DateTime.now();
            });

            events = orderBy(events, 'perf_date');

            if (!events.length) {
                return; // No events
            }

            rawEvents = events;

            events.forEach(event => {
                let eventDate = DateTime.fromISO(event.perf_date);
                let hasMinutes = false;

                if (eventDate.toFormat('m') > 0) {
                    hasMinutes = true;
                }

                let eventTime = hasMinutes ? eventDate.toFormat('h:mma') : eventDate.toFormat('ha');

                const facets = [];
                let tags = [];

                event.keywords.forEach(kw => {
                    facets.push({
                        CategoryTitle: (kw.category === specialOffersText) ? specialOffersText : "Type",
                        Title: kw.keyword,
                        ID: kw.keyword_no
                    });

                    if (kw.category !== specialOffersText) {
                        tags.push({
                            Name: kw.keyword
                        });
                    }
                })

                facets.push({
                    CategoryTitle: "Time",
                    Title: event.time_slot.desc,
                    ID: event.time_slot.id
                })

                let date = DateTime.fromISO(event.perf_date)
                let on_sale_date = DateTime.fromISO(event.on_sale_date);
                let off_sale_date = DateTime.fromISO(event.off_sale_date);
                let onsale = true;

                if (on_sale_date >= DateTime.now() || off_sale_date < DateTime.now()) {
                    onsale = false;
                }

                if (getCookie('_bo_logged_in') != 1 && event.prices.length == 0) {
                    onsale = false;
                }

                const titleArray = event.title.split(" ");

                const data = {
                    date_id: `date-${date.toFormat('yyyy-MM-dd')}`,
                    ID: event.id + "-" + date.ts,
                    PerfID: event.id,
                    Day: eventDate.toFormat('EEE'),
                    Month: eventDate.monthLong,
                    MonthShort: `${eventDate.toFormat('MMM')}`,
                    Year: eventDate.year,
                    Date: `${eventDate.toFormat('LLL')} ${eventDate.toFormat('d')}`,
                    Time: `${eventTime} CT`,
                    Prefix: event.prefix,
                    Suffix: event.suffix,
                    Title: event.title,
                    TitleArray: titleArray,
                    Description: event.description,
                    Image: event.image,
                    Tags: tags,
                    EventPageURL: event.event_page_url,
                    BookingURL: event.perf_book_url,
                    Facets: facets,
                    PerfDate: date,
                    CustomBookingStatus: event.custom_booking.status,
                    CustomBookingLabel: event.custom_booking.label,
                    CustomBookingURL: event.custom_booking.url ? event.custom_booking.url : event.perf_book_url,
                    OnSale: onsale,
                    ProdSeasonNo: event.prod_season_no,
                    PerfNo: event.perf_no,
                    Subscription: event.subscription,
                    ShowBookButton: !event.hide_buy_tickets,
                }

                this.ViewModel.Performances.push(data);
            });
        }).then(() => {
            return getUserFavorites().then(favorites => {
                let performances = this.ViewModel.Performances.filter(item => {
                    return item.Subscription !== false;
                });

                performances = performances.map(item => {
                    return parseInt(item.PerfNo, 10);
                });

                this.ViewModel.EligibleForCYO = intersection(performances, favorites).length;
            });
        }).catch(err => {
            console.error(err);
            throw err;
        }).finally(() => {
            this.init();
        });
    },
    methods: {
        init() {
            setTimeout(() => {
                this.getFilterTypes();
                this.setInitialFilters();
                this.updateCalendar();
                this.ViewModel.Loading = false

            }, 100);
        },
        updateCalendar() {
            const availableDatesForCalendar = [];
            const dates = [];
            const events = this.ViewModel.Performances;

            if (events.length) {
                const firstEvent = events[0];
                const lastEvent = events[events.length - 1];
                const beginningOfMonth = firstEvent.PerfDate < now ? now : firstEvent.PerfDate.startOf('month').startOf('week').minus(1, 'day');
                const endOfLastWeek = lastEvent.PerfDate.endOf('week');

                let date = beginningOfMonth.startOf('day');

                while (date <= endOfLastWeek) {
                    const dateString = date.toFormat('yyyy-MM-dd');
                    const daysEvents = events.filter(event => event.PerfDate.toFormat('yyyy-MM-dd') === dateString);
                    dates.push({
                        date_id: `date-${date.toFormat('yyyy-MM-dd')}`,
                        Month: date.toFormat('MMMM'),
                        Day: date.toFormat('EEEE'),
                        Date: date.toFormat('d'),
                        Year: !date.hasSame(now, 'year') && date.toFormat('yyyy'),
                        Today: date.toFormat('yyyy-MM-dd') === todayString,
                        IsPast: date < now,
                        Events: daysEvents,
                    });
                    if (daysEvents.length > 0) {
                        availableDatesForCalendar.push(date);
                    }
                    date = date.plus({days: 1});
                }
            }
            return availableDatesForCalendar;
        },
        updateURLQuery() {
            const query = this.getURLQuery();
            const url = new URL(location.pathname, location.origin);

            const activeFilters = this.ViewModel.Groups.map(facet => {
                return {
                    Title: facet.Title,
                    Options: facet.Filters.filter(opt => opt.Checked),
                };
            });

            activeFilters.forEach(opt => {
                const params = opt.Options.map(option => {
                    return option.Label;
                });

                url.searchParams.set(opt.Title, params);
            })

            window.history.pushState(query, document.title, url.toString())
        },
        getURLQuery() {
            const queries = {};
            new URL(location.href).searchParams.forEach((value, key) => {
                queries[key] = value;
            })

            return queries;
        },
        setInitialFilters() {
            const query = this.getURLQuery();

            this.ViewModel.Groups.forEach(opt => {
                const filters = query[opt.Title] || null;

                if (filters) {
                    let filtersArr = filters.split(',');
                    opt.Filters.forEach(filter => {
                        if (filtersArr.includes(filter.Label)) {
                            filter.Checked = true;
                        }
                    })
                }
            })
            this.filterResults();
        },
        optionChange() {
            this.filterResults();
            this.updateURLQuery();
            this.updateCalendar();
        },
        filterResults() {
            let activeFilters = this.ViewModel.Groups.map(facet => {
                return {
                    Title: facet.Title,
                    Options: facet.Filters.filter(opt => opt.Checked),
                };
            });

            activeFilters = activeFilters.filter(opt => opt.Options.length)

            this.ViewModel.FilteredEvents = rawEvents.reduce((carry, event) => {
                let isVisible = true;
                activeFilters.forEach(activeFacet => {
                    let facetVisible = false || activeFacet.Options.length === 0;
                    activeFacet.Options.forEach(activeOpt => {
                        if (find(event.Facets, {CategoryTitle: activeFacet.Title, ID: activeOpt.Value})) {
                            facetVisible = true;
                        }
                    });
                    isVisible = isVisible && facetVisible;
                })

                if (isVisible) {
                    carry.push(event);
                }

                return carry;
            }, []);

            if (this.ViewModel.FilteredEvents.length) {
                this.ViewModel.Performances = this.ViewModel.FilteredEvents;
            } else {
                this.ViewModel.Performances = rawEvents;
            }
        },
        getFilterTypes() {
            const timeSlots = [];
            const categories = [];
            const offers = [];
            const map = new Map();
            const timeFilters = [];
            const categoryFilters = [];
            const offerFilters = [];

            for (const item of rawEvents) {
                if(!map.has(item.time_slot.id)){
                    map.set(item.time_slot.id, true);
                    timeSlots.push({
                        id: item.time_slot.id,
                        desc: item.time_slot.desc
                    });
                }
                item.keywords.forEach(kw => {
                    if(!map.has(kw.keyword_no)){
                        map.set(kw.keyword_no, true);

                        if (kw.category === specialOffersText) {
                            offers.push({
                                id: kw.keyword_no,
                                desc: kw.keyword
                            });
                        } else {
                            categories.push({
                                id: kw.keyword_no,
                                desc: kw.keyword
                            });
                        }
                    }
                });
            }

            timeSlots.forEach(time => {
                timeFilters.push({
                    Label: time.desc,
                    Value: time.id,
                    Checked: false
                });
            });

            categories.forEach(time => {
                categoryFilters.push({
                    Label: time.desc,
                    Value: time.id,
                    Checked: false
                });
            });

            offers.forEach(offer => {
                offerFilters.push({
                    Label: offer.desc,
                    Value: offer.id,
                    Checked: false
                })
            });

            this.ViewModel.Groups.push({
                Title: "Type",
                Filters: categoryFilters,
            });

            this.ViewModel.Groups.push({
                Title: "Time",
                Filters: timeFilters,
            });

            this.ViewModel.Groups.push({
                Title: specialOffersText,
                Filters: offerFilters
            })

            rawEvents = this.ViewModel.Performances;

            this.ViewModel.Loading = false;
        },
        getEventImageUrl: eventImage => {
            return 'https://res.cloudinary.com/minnesota-orchestral/image/upload/c_fill,f_auto,g_auto,h_328,q_auto,w_410/' + eventImage.replace(/.*(\/v\d+\/.*)/, '$1');
        },
        getEventModalImageUrl: eventImage => {
            return 'https://res.cloudinary.com/minnesota-orchestral/image/upload/c_fill,f_auto,g_auto,h_660,q_auto,w_825' + eventImage.replace(/.*(\/v\d+\/.*)/, '$1');
        },
        getEventGroups(ViewModel) {
            let years = [];

            for (let performanceIndex in ViewModel.Performances) {
                let date = ViewModel.Performances[performanceIndex];

                let year = date.Year;
                let month = date.Month;
                let dayId = date.PerfDate.day;
                let dayName = date.Day;
                let dayNth = this.getDayNth(dayId);

                let foundYear = years.find(obj => {
                    return obj.Id === year;
                });

                if (!foundYear) {
                    foundYear = {
                        Id: year,
                        Title: year,
                        showTitle: year !== new Date().getFullYear(),
                        Months: []
                    };
                    years.push(foundYear)
                }

                let foundMonth = foundYear.Months.find(obj => {
                    return obj.Id === month;
                });

                if (!foundMonth) {
                    foundMonth = {
                        Id: month,
                        Title: month,
                        Days: []
                    };
                    foundYear.Months.push(foundMonth);
                }

                let foundDay = foundMonth.Days.find(obj => {
                    return obj.Id === dayId;
                });

                if(!foundDay) {
                    foundDay = {
                        Id: dayId,
                        Title: dayName,
                        FullTitle: dayName + ', ' + dayId + dayNth,
                        Performances: []
                    };
                    foundMonth.Days.push(foundDay);
                }
                foundDay.Performances.push(ViewModel.Performances[performanceIndex]);
            }

            return years;
        },
        getDayNth(d) {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1:  return "st";
                case 2:  return "nd";
                case 3:  return "rd";
                default: return "th";
            }
        },
        isDesktopView() {
            return window.matchMedia('(max-width: 1151px)').matches === false;
        },
        onSelect(date) {
            var element = document.querySelector('#date-' + date.time.toFormat('y-LL-dd'));
            if (element) {
                document.querySelector('.date-selector__close').click();
                document.body.classList.remove('filter-open');
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }
});
